/**
 * assets/shipping-protection.js
 * Minimal-intervention approach: never re-renders the cart footer.
 * Only updates the total price and cart badge count from the cart JSON
 * returned by /cart/add.js and /cart/change.js.
 */
(function () {
  if (customElements.get('shipping-protection-toggle')) return;

  function cartFetchConfig() {
    if (typeof fetchConfig === 'function') return fetchConfig('javascript');
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
    };
  }

  function formatMoney(cents) {
    // Uses Shopify.formatMoney if available; falls back to a plain
    // dollar format that matches Dawn's default USD display.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = (window.Shopify.money_format || '${{amount}}');
      return window.Shopify.formatMoney(cents, format);
    }
    const value = (cents / 100).toFixed(2);
    return '$' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function updateTotalPrice(totalCents) {
    // Cart page total
    const totalEls = document.querySelectorAll('.totals__total-value');
    totalEls.forEach((el) => {
      // Preserve currency suffix if present (e.g. "$29.00 USD")
      const currentText = el.textContent.trim();
      const suffixMatch = currentText.match(/[A-Z]{3}$/);
      const suffix = suffixMatch ? ' ' + suffixMatch[0] : '';
      el.textContent = formatMoney(totalCents) + suffix;
    });

    // Cart drawer total (Dawn uses .totals__subtotal-value in drawer)
    const drawerTotalEls = document.querySelectorAll('.totals__subtotal-value');
    drawerTotalEls.forEach((el) => {
      el.textContent = formatMoney(totalCents);
    });
  }

  function updateCartBubble(itemCount) {
    // Refresh only the cart-icon-bubble section (safe: standalone, no
    // internal bindings that break the cart footer).
    fetch(`${window.location.origin}${window.Shopify.routes.root || '/'}?sections=cart-icon-bubble`)
      .then((r) => r.json())
      .then((data) => {
        const html = data['cart-icon-bubble'];
        const target = document.getElementById('cart-icon-bubble');
        if (!html || !target) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newNode = doc.getElementById('cart-icon-bubble');
        if (newNode) target.innerHTML = newNode.innerHTML;
      })
      .catch(() => {});
  }

  class ShippingProtectionToggle extends HTMLElement {
    constructor() {
      super();
      this.checkbox = this.querySelector('.shipping-protection__checkbox');
      this.statusEl = this.querySelector('.shipping-protection__status');
      this.variantId = this.dataset.variantId;
      this.cartAddUrl = this.dataset.cartAddUrl;
      this.cartChangeUrl = this.dataset.cartChangeUrl;
      this.onToggle = this.onToggle.bind(this);
    }

    connectedCallback() {
      if (this.checkbox) this.checkbox.addEventListener('change', this.onToggle);
    }

    disconnectedCallback() {
      if (this.checkbox) this.checkbox.removeEventListener('change', this.onToggle);
    }

    onToggle(event) {
      const wantsAdded = event.target.checked;
      this.setBusy(true);
      this.setStatus(wantsAdded ? 'Adding shipping protection…' : 'Removing shipping protection…');

      const request = wantsAdded ? this.addToCart() : this.removeFromCart();

      request
        .then((cart) => {
          if (cart && typeof cart.total_price === 'number') {
            updateTotalPrice(cart.total_price);
            updateCartBubble(cart.item_count);
          }
          this.setStatus('');
        })
        .catch(() => {
          this.checkbox.checked = !wantsAdded;
          this.setStatus('Something went wrong. Please try again.');
        })
        .finally(() => this.setBusy(false));
    }

    // /cart/add.js returns the added items, so we chain /cart.js to get
    // the full cart (with total_price and item_count) in a single flow.
    addToCart() {
      return fetch(this.cartAddUrl, {
        ...cartFetchConfig(),
        body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('shipping-protection: add failed');
          return response.json();
        })
        .then(() => fetch('/cart.js'))
        .then((r) => r.json());
    }

    // /cart/change.js returns the updated full cart directly.
    removeFromCart() {
      return fetch(this.cartChangeUrl, {
        ...cartFetchConfig(),
        body: JSON.stringify({ id: this.variantId, quantity: 0 }),
      }).then((response) => {
        if (!response.ok) throw new Error('shipping-protection: remove failed');
        return response.json();
      });
    }

    setBusy(isBusy) {
      if (this.checkbox) this.checkbox.disabled = isBusy;
      this.classList.toggle('shipping-protection--loading', isBusy);
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message;
    }
  }

  customElements.define('shipping-protection-toggle', ShippingProtectionToggle);
})();